import edgeCatalog from './edges.json'

export const STRATEGY_COLORS = Object.freeze([
  'RED',
  'BLU',
  'YEL',
  'GRN',
] as const)
export const TILE_KEYS = Object.freeze([
  't1',
  't2',
  't3',
  't4',
  't5',
  't6',
] as const)
export const CENTER_TILE_KEY = 'center' as const
export const EDGE_KEYS = Object.freeze([CENTER_TILE_KEY, ...TILE_KEYS] as const)
export const SIDES = Object.freeze(['TOP', 'RIGHT', 'BOTTOM', 'LEFT'] as const)

export type StrategyColor = (typeof STRATEGY_COLORS)[number]
export type TileKey = (typeof TILE_KEYS)[number]
export type CenterTileKey = typeof CENTER_TILE_KEY
export type EdgeKey = (typeof EDGE_KEYS)[number]
export type Side = (typeof SIDES)[number]
export type TileEdges = readonly [
  StrategyColor,
  StrategyColor,
  StrategyColor,
  StrategyColor,
]
export type SideDelta = readonly [row: number, column: number]

export const EDGES = Object.freeze(
  Object.fromEntries(
    EDGE_KEYS.map((key) => [key, Object.freeze([...edgeCatalog[key]])]),
  ),
) as Readonly<Record<EdgeKey, TileEdges>>

export const SIDE_DELTAS: Readonly<Record<Side, SideDelta>> = Object.freeze({
  TOP: Object.freeze([-1, 0] as const),
  RIGHT: Object.freeze([0, 1] as const),
  BOTTOM: Object.freeze([1, 0] as const),
  LEFT: Object.freeze([0, -1] as const),
})

export const SIDE_INDEX: Readonly<Record<Side, 0 | 1 | 2 | 3>> = Object.freeze({
  TOP: 0,
  RIGHT: 1,
  BOTTOM: 2,
  LEFT: 3,
})

export const OPPOSITE_SIDE: Readonly<Record<Side, Side>> = Object.freeze({
  TOP: 'BOTTOM',
  RIGHT: 'LEFT',
  BOTTOM: 'TOP',
  LEFT: 'RIGHT',
})

export function isStrategyColor(value: string): value is StrategyColor {
  return STRATEGY_COLORS.includes(value as StrategyColor)
}

export function isTileKey(value: string): value is TileKey {
  return TILE_KEYS.includes(value as TileKey)
}

export function isEdgeKey(value: string): value is EdgeKey {
  return Object.prototype.hasOwnProperty.call(EDGES, value)
}

export function isSide(value: string): value is Side {
  return SIDES.includes(value as Side)
}

export function rotateEdges(edges: TileEdges, rotation: number): TileEdges
export function rotateEdges(
  edges: readonly string[],
  rotation: number,
): string[]
export function rotateEdges(
  edges: readonly string[],
  rotation: number,
): TileEdges | string[] {
  if (!Number.isInteger(rotation)) {
    throw new RangeError('Tile rotation must be an integer')
  }

  const [top, right, bottom, left] = edges
  const normalizedRotation = ((rotation % 4) + 4) % 4
  if (normalizedRotation === 1) return [left, top, right, bottom]
  if (normalizedRotation === 2) return [bottom, left, top, right]
  if (normalizedRotation === 3) return [right, bottom, left, top]
  return [top, right, bottom, left]
}
